import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import CommonModal from '../Common/Modal/Modal';
import TextInput from '../FormInputs/TextInput/TextInput';
import PasswordInput from '../FormInputs/PasswordInput/PasswordInput';
import CustomSelect from '../FormInputs/CustomSelect/CustomSelect';
import { capitalizeFirstLetter } from '../../utils/api';
import { login } from '../../utils/api/login';
import Button from '../Button/Button';
import Multiselect from '../FormInputs/Multiselect/Multiselect';
import { useUnsavedChangesWarning, useCloseDialogWithUnsavedChanges } from '../../hooks';

const AddEditUserModal = ({
  onCloseModal,
  userData = undefined,
  onSubmit,
  errorMessage,
  isDisabled,
  setErrorMessage,
  organization,
  onDeactivate,
  currentUserId = null,
  currentUserEmail = '',
  isCurrentUserManager = false,

}) => {
  // Get the user's role for the currently selected organization,
  const initialFormData = useMemo(() => {
    const initialRole = userData?.user_organizations?.find(
      uo => uo.organization_id === organization.id,
    )?.role || '';

    return {
      name_first: userData?.name_first || '',
      name_last: userData?.name_last || '',
      email: userData?.email || '',
      role: initialRole,
    };
  }, [userData, organization.id]);

  const [formData, setFormData] = useState(initialFormData);

  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [isVerifyingPassword, setIsVerifyingPassword] = useState(false);

  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);

  const hasUnsavedChanges = !isEqual(initialFormData, formData);

  useEffect(() => {
    setIsUnsavedChanges(hasUnsavedChanges);
  }, [hasUnsavedChanges]); // userData - track changes to userData for unsaved changes if isUnsavedChanges was set to false on previous changes

  useUnsavedChangesWarning(isUnsavedChanges);

  const handleCloseDialog = useCloseDialogWithUnsavedChanges({
    isUnsavedChanges: hasUnsavedChanges,
    setIsUnsavedChanges,
    redirectCallback: onCloseModal,
  });

  const selectTextOptions = [
    {
      label: 'user',
      comment: 'Can only conduct outreach',
    },
    {
      label: 'planner',
      comment: 'Can conduct and plan outreach',
    },
    {
      label: 'manager',
      comment: 'Can conduct and plan outreach, delete sites, and manage users',
    },
  ];

  const [isDeactivate, setIsDeactivate] = useState(false);
  const [isEmailChangeConfirmModalOpen, setIsEmailChangeConfirmModalOpen] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState([
    { label: 'I understand', value: 'confirm', selected: false },
  ]);
  const isConfirmed = confirmDeactivate.find(opt => opt.value === 'confirm')?.selected;
  const disableButton = isDeactivate ? !isConfirmed : (isDisabled);

  const options = selectTextOptions.map((option) => ({
    value: option.label,
    label: capitalizeFirstLetter(option.label),
    comment: option.comment,
  }));

  const inputTextPadding = 'px-2';

  const submitAddEditUser = (extraData = {}) => {
    onSubmit(userData.id, { ...formData, ...extraData });
  };

  const handleSubmit = () => {
    if (isDeactivate) {
      onDeactivate(userData);
      return;
    }

    const isEditingExistingUser = Boolean(userData?.id);
    const normalizedInitialEmail = (initialFormData.email || '').trim().toLowerCase();
    const normalizedUpdatedEmail = (formData.email || '').trim().toLowerCase();
    const isEmailChanged = normalizedInitialEmail !== normalizedUpdatedEmail;
    const isEditingOtherUser = Boolean(currentUserId)
      && Number(userData.id) !== Number(currentUserId);

    if (isEditingExistingUser && isCurrentUserManager && isEditingOtherUser && isEmailChanged) {
      setConfirmPassword('');
      setConfirmPasswordError('');
      setIsEmailChangeConfirmModalOpen(true);
      return;
    }

    submitAddEditUser();
  };

  const handleUserDataChange = (field, value) => {
    // clear error message if user starts to fill the field
    setErrorMessage({
      name_first: '', name_last: '', email: '', role: '',
    });
    setFormData((prev) => {
      // Only update state if the new value is different
      if (prev[field] === value) return prev;
      return { ...prev, [field]: value };
    });
  };
  const labelTextSize = 'text-sm font-medium normal-case';
  const inputFontSize = 'text-sm';
  const errorMessageClass = 'absolute';

  const changeTitle = userData?.id ? 'Edit User' : 'Invite User';
  const submitButton = userData?.id ? 'Save' : 'Invite';

  const getButtonColor = () => {
    if (isDeactivate) {
      return 'primary-delete';
    }
    return 'primary';
  };
  return (
    <CommonModal
      boxClassName="w-5/12 md:w-4/12 md:min-w-[478px]"
      title={isDeactivate ? 'Deactivate User' : changeTitle}
      handleClose={handleCloseDialog}
      className=""
      shadow="shadow-vamos-drop-shadow"
      textAlignment="text-left"
      titlePosition="left"
      closeIconPosition=""
      headerClassName="p-5.5"
      maxHeight=""
      titleClassName={isDeactivate ? 'text-vamos-red' : 'text-vamos-black'}
      overflowY=""
      childrenHeight=""
    >
      <div>
        {!isDeactivate && (
          <div className="px-5.5 py-8 overflow-y-auto max-h-[65vh]">
            {!userData?.id && (
              <div className="bg-vamos-v-light-gray rounded-10px pt-5.5 pl-5.5 pb-7.5 pr-11.5 flex">
                <div>
                  <p className="text-lg"> Once the invite is sent, the user will receive an email with a link to set their password and log in. If they do not receive the email, please ask them to check their junk mail folder.</p>
                  <p className="text-base font-bold text-vamos-red pt-6">The link will expire in 24 hours.</p>
                </div>
              </div>
            )}

            <div>
              <div className="grid gap-5.5">
                <TextInput
                  label="First Name"
                  name="first-name"
                  type="text"
                  labelTextSize={labelTextSize}
                  isRequired
                  asteriskWidth="w-2"
                  value={formData.name_first}
                  setValue={value => handleUserDataChange('name_first', value)}
                  errorMessage={errorMessage.name_first}
                  errorMessageClass={errorMessageClass}
                  placeholder="enter first name"
                  spaceY="space-y-1"
                  fontSize={inputFontSize}
                  inputTextPadding={inputTextPadding}
                />
                <TextInput
                  label="Last Name"
                  name="last-name"
                  type="text"
                  labelTextSize={labelTextSize}
                  isRequired
                  asteriskWidth="w-2"
                  value={formData.name_last}
                  setValue={value => handleUserDataChange('name_last', value)}
                  errorMessage={errorMessage.name_last}
                  errorMessageClass={errorMessageClass}
                  placeholder="enter last name"
                  spaceY="space-y-1"
                  fontSize={inputFontSize}
                  inputTextPadding={inputTextPadding}
                />
                <TextInput
                  label="Email"
                  name="email"
                  type="email"
                  dataTestId="users-modal-email-input"
                  labelTextSize={labelTextSize}
                  isRequired
                  asteriskWidth="w-2"
                  value={formData.email}
                  setValue={value => handleUserDataChange('email', value)}
                  validationError={errorMessage.email} // Validation error for email format
                  errorMessageClass={errorMessageClass}
                  placeholder="enter email address"
                  spaceY="space-y-1"
                  fontSize={inputFontSize}
                  inputTextPadding={inputTextPadding}
                />
                <CustomSelect
                  label={`Role in ${organization.name}`}
                  name="role"
                  options={options}
                  isRequired
                  value={formData.role}
                  setValue={value => handleUserDataChange('role', value?.label)}
                  errorMessage={errorMessage.role}
                  errorMessageClass={errorMessageClass}
                  placeholder="Select"
                  spaceY="space-y-1"
                  labelFontSize={labelTextSize}
                  fontSize={inputFontSize}
                  menuPosition="relative"
                />

              </div>
            </div>

          </div>
        )}
        {isDeactivate && (
          <div className="pt-6 px-13.5 pb-10 leading-[1.2] font-normal"> <p className="mb-5.5">By deactivating <strong>{formData.name_first} {formData.name_last}</strong>, the user will be removed from this Vamos workspace and will no longer have access to {organization.name}’s Vamos data.</p>
            <Multiselect
              label=""
              options={confirmDeactivate}
              setValue={(updatedOptions) => setConfirmDeactivate(updatedOptions)}
            />
          </div>

        )}
        <div className="flex p-5.5 gap-x-3 border-t border-vamos-light-gray min-h-fit">
          {userData?.id && !isDeactivate && (
            <div>
              <Button
                label="Deactivate user..."
                color="secondary-delete"
                size="md"
                className="!p-0 !m-0 !justify-start"
                onClick={() => setIsDeactivate(true)}
              />
            </div>
          )}
          <div className="flex-1" />
          <Button
            label="Cancel"
            type="button"
            size="md"
            color="secondary"
            onClick={() => {
              if (isDeactivate) {
                setIsDeactivate(false);
                setConfirmDeactivate([
                  { label: 'I understand', value: 'confirm', selected: false },
                ]);
              } else {
                handleCloseDialog();
              }
            }}
          />
          <Button label={isDeactivate ? 'Deactivate' : submitButton} size="md" onClick={handleSubmit} isDisabled={disableButton} color={getButtonColor()} />
        </div>
      </div>

      {isEmailChangeConfirmModalOpen && (
        <CommonModal
          title="Confirm action"
          handleClose={() => setIsEmailChangeConfirmModalOpen(false)}
          boxClassName="w-11/12 md:w-[420px]"
          minWidth=""
          textAlignment="text-left"
          titlePosition="left"
          closeIconPosition=""
          headerClassName="p-5.5"
          maxHeight=""
          overflowY=""
          childrenHeight=""
        >
          <div className="p-5.5 pt-4">
            <p className="text-sm mb-4">
              To change this user’s email, please enter your password.
            </p>

            <PasswordInput
              name="confirm-password"
              label="Password"
              isRequired
              dataTestId="users-modal-confirm-password-input"
              value={confirmPassword}
              setValue={(value) => {
                setConfirmPassword(value);
                setConfirmPasswordError('');
              }}
              placeholder="enter your password"
              className="w-full"
              validationErrorMessage={confirmPasswordError}
              isValidationBorderEnabled
              shouldHideConfirmedIcon
            />

            <div className="flex justify-end gap-x-3 mt-5">
              <Button
                label="Cancel"
                type="button"
                size="md"
                color="secondary"
                onClick={() => {
                  setIsEmailChangeConfirmModalOpen(false);
                  setConfirmPassword('');
                  setConfirmPasswordError('');
                }}
              />
              <Button
                label="Confirm"
                size="md"
                dataTestId="users-modal-confirm-email-change-button"
                isDisabled={isVerifyingPassword}
                onClick={async () => {
                  if (!confirmPassword.trim()) {
                    setConfirmPasswordError('Required');
                    return;
                  }

                  if (!currentUserEmail) {
                    setConfirmPasswordError('Unable to verify password. Please refresh and try again.');
                    return;
                  }

                  setIsVerifyingPassword(true);
                  try {
                    const normalizedCurrentUserEmail = currentUserEmail.trim().toLowerCase();
                    const result = await login(normalizedCurrentUserEmail, confirmPassword);

                    if (!result.success) {
                      setConfirmPasswordError(result.error || 'Invalid password');
                      return;
                    }

                    setIsEmailChangeConfirmModalOpen(false);
                    submitAddEditUser({
                      email: formData.email,
                      isUpdatedByManager: true,
                    });
                  } finally {
                    setIsVerifyingPassword(false);
                  }
                }}
              />
            </div>
          </div>
        </CommonModal>
      )}
    </CommonModal>
  );
};

AddEditUserModal.propTypes = {
  onCloseModal: PropTypes.func.isRequired,
  userData: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  errorMessage: PropTypes.shape({
    name_first: PropTypes.string,
    name_last: PropTypes.string,
    email: PropTypes.string,
    role: PropTypes.string,
  }).isRequired,
  isDisabled: PropTypes.bool.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  organization: PropTypes.object.isRequired,
  onDeactivate: PropTypes.func.isRequired,
  currentUserId: PropTypes.number,
  currentUserEmail: PropTypes.string,
  isCurrentUserManager: PropTypes.bool,
};

export default AddEditUserModal;
